import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Layers,
  Star,
  Globe,
  Lock,
  Copy,
  Eye,
  Filter,
} from "lucide-react";
import {
  Card,
  Segmented,
  SearchField,
  Button,
  Badge,
  Empty,
  Spinner,
  PageHeader,
  useToast,
} from "@/components/ui";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";

type Tier = "FREE" | "PRO";
type Category =
  | "romantic" | "playful" | "adventure" | "elegant"
  | "whimsical" | "serene" | "heartfelt" | "cinematic";

interface BasicTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  thumbnailUrl: string | null;
  category: Category | null;
  tier: Tier;
  slideCount: number;
  usageCount: number;
  isFeatured: boolean;
  isPublic: boolean;
  proposalTypes: string[];
  translations: { locale: string; name: string; description: string | null }[];
}

interface MoodTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  shortBlurb: string | null;
  thumbnailUrl: string | null;
  musicStyle: string;
  isFeatured: boolean;
  isActive: boolean;
  displayOrder: number;
  slideCount: number;
}

const CATEGORIES: Category[] = [
  "romantic", "playful", "adventure", "elegant",
  "whimsical", "serene", "heartfelt", "cinematic",
];

const CATEGORY_EMOJI: Record<Category, string> = {
  romantic: "💕",
  playful: "🎈",
  adventure: "🗺️",
  elegant: "🥂",
  whimsical: "✨",
  serene: "🌙",
  heartfelt: "💌",
  cinematic: "🎬",
};

const CATEGORY_GRADIENT: Record<Category, string> = {
  romantic: "linear-gradient(135deg, #FF6B9D 0%, #D60056 100%)",
  playful: "linear-gradient(135deg, #FFB340 0%, #FF6B6B 100%)",
  adventure: "linear-gradient(135deg, #4ADE80 0%, #0EA5E9 100%)",
  elegant: "linear-gradient(135deg, #C084FC 0%, #7C3AED 100%)",
  whimsical: "linear-gradient(135deg, #67E8F9 0%, #3B82F6 100%)",
  serene: "linear-gradient(135deg, #818CF8 0%, #4F46E5 100%)",
  heartfelt: "linear-gradient(135deg, #FB7185 0%, #BE185D 100%)",
  cinematic: "linear-gradient(135deg, #FFD93D 0%, #FF6B6B 50%, #C084FC 100%)",
};

const TIER_LABEL = {
  FREE: "Free",
  PRO: "Pro",
} as const;

export default function TemplatesPage() {
  const [basic, setBasic] = useState<BasicTemplate[]>([]);
  const [mood, setMood] = useState<MoodTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"basic" | "mood">("basic");
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const { showToast } = useToast();

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, m] = await Promise.all([
        adminApi.get<{ success: boolean; templates: BasicTemplate[] }>("/admin/templates/basic"),
        adminApi.get<{ success: boolean; templates: MoodTemplate[] }>("/admin/templates/mood"),
      ]);
      if (b.success) setBasic(b.templates);
      if (m.success) setMood(m.templates);
    } catch (e: any) {
      setError(e.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (t: BasicTemplate | MoodTemplate, kind: "basic" | "mood") => {
    if (!confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/templates/${kind}/${t.id}`);
      showToast({ title: "Template deleted", variant: "success" });
      fetchAll();
    } catch (e: any) {
      showToast({ title: "Failed to delete template", variant: "error" });
      setError(e.message || "Failed to delete template");
    }
  };

  const handleDuplicate = async (t: BasicTemplate) => {
    try {
      const res = await adminApi.get<{ success: boolean; template: BasicTemplate }>(
        `/admin/templates/basic/${t.id}`,
      );
      if (!res.success) return;
      const full = res.template as any;
      const newSlug = `${t.slug}-copy-${Date.now().toString(36)}`;
      await adminApi.post("/admin/templates/basic", {
        slug: newSlug,
        name: `${t.name} (copy)`,
        description: t.description,
        thumbnailUrl: t.thumbnailUrl,
        category: t.category,
        tier: t.tier,
        isPublic: false,
        isFeatured: false,
        proposalTypes: t.proposalTypes,
        slides: full.slides ?? [],
        translations: t.translations,
      });
      showToast({ title: "Template duplicated", variant: "success" });
      fetchAll();
    } catch (e: any) {
      showToast({ title: "Failed to duplicate template", variant: "error" });
      setError(e.message || "Failed to duplicate template");
    }
  };

  const filteredBasic = useMemo(() => basic.filter((t) => {
    if (search && !`${t.slug} ${t.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory && t.category !== filterCategory) return false;
    return true;
  }), [basic, search, filterCategory]);

  const filteredMood = useMemo(() => mood.filter((t) => {
    if (search && !`${t.slug} ${t.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [mood, search]);

  const tabCounts = (
    <Segmented<"basic" | "mood">
      value={tab}
      options={[
        { value: "basic", label: <>Basic <span className="opacity-60 ml-1">{basic.length}</span></> },
        { value: "mood", label: <>Mood <span className="opacity-60 ml-1">{mood.length}</span></> },
      ]}
      onChange={setTab}
    />
  );

  return (
    <div>
      <PageHeader
        eyebrow="Library"
        title="Templates"
        subtitle={`${basic.length + mood.length} reusable templates · curated for every moment`}
        actions={
          <Button
            variant="primary"
            onClick={() => {
              const params = new URLSearchParams();
              if (tab === "mood") params.set("kind", "mood");
              window.location.href = `/templates/new?${params.toString()}`;
            }}
          >
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        }
      />

      {/* Toolbar */}
      <Card className="!p-3 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          {tabCounts}
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search by name or slug…"
          />
          {tab === "basic" && (
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="field-select cursor-pointer"
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
        </div>
      </Card>

      {error && (
        <Card tint="rose" className="mb-4">
          <p className="text-sm text-[var(--color-red)]">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="template-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <TemplateCardSkeleton key={i} />
          ))}
        </div>
      ) : tab === "basic" ? (
        <BasicGrid
          templates={filteredBasic}
          onDelete={(t) => handleDelete(t, "basic")}
          onDuplicate={handleDuplicate}
        />
      ) : (
        <MoodGrid
          templates={filteredMood}
          onDelete={(t) => handleDelete(t, "mood")}
        />
      )}
    </div>
  );
}

/* ─── Basic template card grid (2:3 ratio) ─────────────────────── */
function BasicGrid({
  templates,
  onDelete,
  onDuplicate,
}: {
  templates: BasicTemplate[];
  onDelete: (t: BasicTemplate) => void;
  onDuplicate: (t: BasicTemplate) => void;
}) {
  if (templates.length === 0) {
    return (
      <Card>
        <Empty
          icon={<Layers className="h-5 w-5" />}
          title="No basic templates"
          message="Templates you create will appear here as a beautiful grid."
        />
      </Card>
    );
  }

  return (
    <div className="template-grid">
      {templates.map((t) => (
        <TemplateCard
          key={t.id}
          template={t}
          onDelete={() => onDelete(t)}
          onDuplicate={() => onDuplicate(t)}
        />
      ))}
    </div>
  );
}

/* ─── Mood template card grid ────────────────────────────────────── */
function MoodGrid({
  templates,
  onDelete,
}: {
  templates: MoodTemplate[];
  onDelete: (t: MoodTemplate) => void;
}) {
  if (templates.length === 0) {
    return (
      <Card>
        <Empty
          icon={<Sparkles className="h-5 w-5" />}
          title="No mood templates"
          message="Mood templates add the AI music styling layer to proposals."
        />
      </Card>
    );
  }

  return (
    <div className="template-grid">
      {templates.map((t) => (
        <MoodCard key={t.id} template={t} onDelete={() => onDelete(t)} />
      ))}
    </div>
  );
}

/* ─── Card visuals ──────────────────────────────────────────────── */
function TemplateCard({
  template: t,
  onDelete,
  onDuplicate,
}: {
  template: BasicTemplate;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const gradient = t.category ? CATEGORY_GRADIENT[t.category] : "linear-gradient(135deg, #FF6B9D 0%, #C084FC 100%)";
  const emoji = t.category ? CATEGORY_EMOJI[t.category] : "✨";

  return (
    <article
      className="template-card"
      onClick={() => (window.location.href = `/templates/${t.id}`)}
    >
      {/* Cover — 2:3 ratio */}
      <div
        className="template-card-cover"
        style={t.thumbnailUrl ? { background: gradient } : undefined}
      >
        {t.thumbnailUrl ? (
          <div
            className="template-card-cover-img"
            style={{ backgroundImage: `url(${t.thumbnailUrl})` }}
          />
        ) : (
          <div className="template-card-cover-placeholder">
            <span>{emoji}</span>
          </div>
        )}

        {/* Top tier badge */}
        <div className="template-card-badge-top">
          <span className={cn("template-card-tag", `tier-${t.tier.toLowerCase()}`)}>
            {TIER_LABEL[t.tier]}
          </span>
        </div>

        {/* Featured star */}
        {t.isFeatured ? (
          <div className="template-card-star">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          </div>
        ) : !t.isPublic ? (
          <div className="template-card-star">
            <Lock className="h-3.5 w-3.5 text-white/70" />
          </div>
        ) : null}

        {/* Bottom slide count */}
        <div className="template-card-badge-bottom">
          <span className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[10.5px] font-semibold text-white backdrop-blur">
            <Layers className="h-3 w-3" />
            {t.slideCount} slides
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="template-card-info">
        <div className="template-card-title">{t.name}</div>
        {t.description ? (
          <p className="text-[12px] leading-relaxed text-[var(--color-tertiary-label)] line-clamp-2">
            {t.description}
          </p>
        ) : null}

        <div className="template-card-meta">
          <span className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            {t.usageCount.toLocaleString()}
          </span>
          <span>·</span>
          <span className="truncate">{t.category ?? "—"}</span>
          {t.translations.length > 0 && (
            <>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                {t.translations.length}
              </span>
            </>
          )}
        </div>

        <div className="template-card-actions">
          <Button
            variant="outline"
            size="sm"
            iconOnly
            className="cursor-pointer"
            title="Edit"
            aria-label="Edit template"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/templates/${t.id}`;
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="cursor-pointer"
            title="Duplicate"
            aria-label="Duplicate template"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="cursor-pointer ml-auto"
            title="Delete"
            aria-label="Delete template"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-[var(--color-red)]" />
          </Button>
        </div>
      </div>
    </article>
  );
}

function MoodCard({
  template: t,
  onDelete,
}: {
  template: MoodTemplate;
  onDelete: () => void;
}) {
  return (
    <article
      className="template-card"
      onClick={() => (window.location.href = `/templates/${t.id}?kind=mood`)}
    >
      <div
        className="template-card-cover"
        style={{ background: "linear-gradient(135deg, #C084FC 0%, #1a0a14 100%)" }}
      >
        {t.thumbnailUrl ? (
          <div
            className="template-card-cover-img"
            style={{ backgroundImage: `url(${t.thumbnailUrl})` }}
          />
        ) : (
          <div className="template-card-cover-placeholder">
            <span>🎵</span>
          </div>
        )}

        <div className="template-card-badge-top">
          <span className="template-card-tag tier-premium">
            {t.musicStyle}
          </span>
        </div>

        {t.isFeatured ? (
          <div className="template-card-star">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          </div>
        ) : null}

        <div className="template-card-badge-bottom">
          <span className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[10.5px] font-semibold text-white backdrop-blur">
            <Sparkles className="h-3 w-3" />
            {t.slideCount} slides
          </span>
        </div>
      </div>

      <div className="template-card-info">
        <div className="template-card-title">{t.name}</div>
        {t.shortBlurb ? (
          <p className="text-[12px] leading-relaxed text-[var(--color-tertiary-label)] line-clamp-2">
            {t.shortBlurb}
          </p>
        ) : null}

        <div className="template-card-meta">
          <span>{t.musicStyle}</span>
          {!t.isActive && (
            <>
              <span>·</span>
              <Badge variant="error">Inactive</Badge>
            </>
          )}
        </div>

        <div className="template-card-actions">
          <Button
            variant="outline"
            size="sm"
            iconOnly
            className="cursor-pointer"
            title="Edit"
            aria-label="Edit template"
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `/templates/${t.id}?kind=mood`;
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="cursor-pointer ml-auto"
            title="Delete"
            aria-label="Delete template"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-[var(--color-red)]" />
          </Button>
        </div>
      </div>
    </article>
  );
}

function TemplateCardSkeleton() {
  return (
    <div className="template-card pointer-events-none">
      <div className="template-card-cover skeleton" />
      <div className="template-card-info space-y-2">
        <div className="skeleton h-4 w-3/4 rounded" />
        <div className="skeleton h-3 w-full rounded" />
        <div className="skeleton h-3 w-2/3 rounded" />
        <div className="flex gap-2 pt-2">
          <div className="skeleton h-7 w-7 rounded" />
          <div className="skeleton h-7 w-7 rounded" />
          <div className="skeleton ml-auto h-7 w-7 rounded" />
        </div>
      </div>
    </div>
  );
}